// src/pages/Payroll.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, doc, setDoc, getDoc, writeBatch } from 'firebase/firestore';import { db } from '../firebase';
import { format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { calculateCommission, DEFAULT_COMP_PLAN } from '../data/compPlans';
import { isChargeback } from '../data/chargebacks';

const CHARGEBACK_LIMITS = { 'Agent': 5000, 'Team Lead': 5000, 'Recruiter': 2000, 'RM': 20000, 'admin': 20000 };

// What Spectrum pays per product (invoice rates)
const INVOICE_RATES = {
  'gig': 675,
  'internet_premier': 600,
  'internet_assist': 150,
  'tv_select': 100,
  'tv_choice': 50,
  'mobile_line1': 150,
  'mobile_lines2to10': 250,
  'uld_plus': 25,
};

function getInvoiceRate(productId, quantity) {
  return (INVOICE_RATES[productId] || 0) * (quantity || 1);
}
function fmt(n) { return (n < 0 ? '-$' : '$') + Math.abs(Math.round(n)).toLocaleString(); }
function getBucketLimit(role) { return CHARGEBACK_LIMITS[role] || 5000; }

export default function Payroll() {
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [compPlans, setCompPlans] = useState([DEFAULT_COMP_PLAN]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [addModal, setAddModal] = useState(null);
  const [modalForm, setModalForm] = useState({ amount: '', note: '' });
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const weekStart = startOfWeek(subWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
  const weekEnd = endOfWeek(subWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
  const weekKey = format(weekStart, 'yyyy-MM-dd');

  useEffect(() => {
    async function load() {
      const [ordSnap, repSnap, planSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'compPlans')),
      ]);
      setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const dbPlans = planSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (dbPlans.length) setCompPlans([DEFAULT_COMP_PLAN, ...dbPlans]);
      setLoading(false);
    }
    load();
  }, []);

  useEffect(() => {
    async function loadWeek() {
      try {
        const [weekSnap, bucketSnap] = await Promise.all([
          getDoc(doc(db, 'payrollWeeks', weekKey)),
          getDocs(collection(db, 'repBuckets')),
        ]);
        // Build bucket map from persisted balances
        const bucketMap = {};
        bucketSnap.docs.forEach(d => { bucketMap[d.id] = d.data().bucketBalance || 0; });

        if (weekSnap.exists()) {
          setPayrollData(weekSnap.data().repData || {});
          setSubmitted(weekSnap.data().submitted || false);
        } else {
          // New week — seed bucket balances from persisted rep buckets
          const seedData = {};
          Object.entries(bucketMap).forEach(([repId, balance]) => {
            seedData[repId] = { additions: [], deductions: [], advances: [], chargebackBucketUsed: balance, heldPay: 0 };
          });
          setPayrollData(seedData);
          setSubmitted(false);
        }
      } catch(e) { setPayrollData({}); setSubmitted(false); }
    }
    if (!loading) loadWeek();
  }, [weekKey, loading]);

  const weekOrders = orders.filter(o => {
    if (o.heldFromPayroll) return false;
    // Use the order's actual date (Internet Usage Date from CSV) if available
    // Fall back to createdAt if no date field
    let orderDate;
    if (o.date && o.date !== '' && o.date !== '1901-01-01') {
      orderDate = new Date(o.date);
    } else if (o.createdAt?.seconds) {
      orderDate = new Date(o.createdAt.seconds * 1000);
    } else {
      return false;
    }
    return orderDate >= weekStart && orderDate <= weekEnd;
  });

  function getRepData(repId) {
    return payrollData[repId] || { additions: [], deductions: [], advances: [], chargebackBucketUsed: 0, heldPay: 0 };
  }

  // Recalculate commissions live for all week orders
  const liveCommissions = weekOrders.map(o => ({
    order: o,
    commissions: calculateCommission(o, reps, compPlans),
  }));

  const repRows = reps.map(rep => {
    // Find all orders where this rep earns (via explicit override or auto-earn)
    const myEarnings = [];
    const myOrders = [];
    liveCommissions.forEach(({ order, commissions: comms }) => {
      const myComm = comms.find(c => c.repId === rep.id);
      if (myComm) {
        myEarnings.push({ order, amount: myComm.amount });
        myOrders.push(order);
      }
    });
    const commissions = myEarnings.reduce((a, e) => a + e.amount, 0);
    // Chargebacks = orders that disconnected in this pay week AND within 90 days of activation
    const chargebacks = myEarnings
      .filter(e => {
        if (!isChargeback(e.order)) return false;
        const discDate = new Date(e.order.disconnectDate);
        return discDate >= weekStart && discDate <= weekEnd;
      })
      .reduce((a, e) => a + e.amount, 0);
    const pd = getRepData(rep.id);
    const additions = (pd.additions || []).reduce((a, e) => a + Number(e.amount), 0);
    const deductions = (pd.deductions || []).reduce((a, e) => a + Number(e.amount), 0);
    const advances = (pd.advances || []).reduce((a, e) => a + Number(e.amount), 0);
    const heldPay = Number(pd.heldPay || 0);
    const bucketLimit = getBucketLimit(rep.role);

    // Bucket balance = previously accumulated + 10% of this week's commissions
    // But only withhold up to the remaining space in the bucket
    const bucketUsed = Number(pd.chargebackBucketUsed || 0);
    const bucketSpace = Math.max(0, bucketLimit - bucketUsed);
    const tenPctThisWeek = Math.round(commissions * 0.10);
    const bucketWithheldThisWeek = Math.min(tenPctThisWeek, bucketSpace);
    const bucketTotal = bucketUsed + bucketWithheldThisWeek; // total bucket balance after this week

    // Chargebacks draw from bucket first
    const absorbedByBucket = Math.min(chargebacks, bucketTotal);
    const chargebacksOwed = Math.max(0, chargebacks - absorbedByBucket);

    // Net pay = commissions minus bucket withholding minus chargebacks not covered by bucket
    const total = commissions - bucketWithheldThisWeek + additions - deductions - chargebacksOwed - advances - heldPay;

    return { rep, myOrders, commissions, chargebacks, chargebacksOwed, absorbedByBucket,
      bucketUsed, bucketTotal, bucketLimit, bucketWithheldThisWeek,
      pd, additions, deductions, advances, heldPay, total };
  }).filter(r => r.myOrders.length > 0 || r.additions > 0 || r.deductions > 0 || r.advances > 0);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  function SortTh({ col, label }) {
    const active = sortCol === col;
    return (
      <th style={{ ...S.th, cursor: 'pointer', userSelect: 'none', color: active ? '#00d4a0' : '#555' }}
        onClick={() => toggleSort(col)}>
        {label} {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </th>
    );
  }

  const filteredRows = repRows
    .filter(r => !search || r.rep.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case 'name':        av = a.rep.name; bv = b.rep.name; break;
        case 'commissions': av = a.commissions; bv = b.commissions; break;
        case 'additions':   av = a.additions; bv = b.additions; break;
        case 'chargebacksTotal': av = a.chargebacks; bv = b.chargebacks; break;
        case 'chargebacks': av = a.chargebacksOwed; bv = b.chargebacksOwed; break;
        case 'advances':    av = a.advances; bv = b.advances; break;
        case 'deductions':  av = a.deductions; bv = b.deductions; break;
        case 'total':       av = a.total; bv = b.total; break;
        case 'orders':      av = a.myOrders.length; bv = b.myOrders.length; break;
        default:            av = a.rep.name; bv = b.rep.name;
      }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  const totals = repRows.reduce((a, r) => ({    commissions: a.commissions + r.commissions, additions: a.additions + r.additions,
    deductions: a.deductions + r.deductions, chargebacks: a.chargebacks + r.chargebacksOwed,
    advances: a.advances + r.advances, total: a.total + r.total,
  }), { commissions:0, additions:0, deductions:0, chargebacks:0, advances:0, total:0 });

  // Invoice total = what Spectrum pays us
  const invoiceTotal = weekOrders.reduce((a, o) => a + getInvoiceRate(o.productId, o.quantity), 0);

  const [recalculating, setRecalculating] = useState(false);
  const [recalcDone, setRecalcDone] = useState(null);

  async function recalculateCommissions() {
    setRecalculating(true);
    setRecalcDone(null);
    const ordSnap = await getDocs(collection(db, 'orders'));
    const allOrders = ordSnap.docs.map(d => ({ docId: d.id, ...d.data() }));
    const toFix = allOrders.filter(o => !o.totalCommission || o.totalCommission === 0);

    let fixed = 0;
    let currentBatch = writeBatch(db);
    let batchCount = 0;

    for (const order of toFix) {
      const rep = reps.find(r =>
        r.id === order.repId ||
        r.name === order.repName ||
        (order.agentCode && r.agentCode === order.agentCode)
      );
      if (!rep) continue;

      const overrides = [{ repId: rep.id, role: rep.role || 'Agent' }];
      const commissions = calculateCommission(
        { productId: order.productId, quantity: order.quantity || 1, overrides, compPlanId: rep.compPlanId || DEFAULT_COMP_PLAN.id },
        reps, [DEFAULT_COMP_PLAN]
      );
      const totalCommission = commissions.reduce((a, c) => a + c.amount, 0);
      if (totalCommission <= 0) continue;

      currentBatch.update(doc(db, 'orders', order.docId), { repId: rep.id, repName: rep.name, commissions, totalCommission, overrides });
      batchCount++;
      fixed++;

      if (batchCount >= 400) {
        await currentBatch.commit();
        currentBatch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) await currentBatch.commit();

    const newSnap = await getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc')));
    setOrders(newSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setRecalcDone(fixed);
    setRecalculating(false);
  }

  async function savePayroll(newData) {
    await setDoc(doc(db, 'payrollWeeks', weekKey), { weekStart: weekStart.toISOString(), weekEnd: weekEnd.toISOString(), repData: newData, submitted, updatedAt: new Date().toISOString() });
  }

  async function addEntry(repId, type) {
    if (!modalForm.amount) return;
    setSaving(true);
    const rd = getRepData(repId);
    const entry = { amount: Number(modalForm.amount), note: modalForm.note, date: new Date().toISOString() };
    const key = type === 'addition' ? 'additions' : type === 'deduction' ? 'deductions' : 'advances';
    const updated = { ...payrollData, [repId]: { ...rd, [key]: [...(rd[key] || []), entry] } };
    setPayrollData(updated);
    await savePayroll(updated);
    setAddModal(null); setModalForm({ amount: '', note: '' }); setSaving(false);
  }

  async function removeEntry(repId, type, idx) {
    const rd = getRepData(repId);
    const key = type === 'addition' ? 'additions' : type === 'deduction' ? 'deductions' : 'advances';
    const updated = { ...payrollData, [repId]: { ...rd, [key]: (rd[key] || []).filter((_, i) => i !== idx) } };
    setPayrollData(updated); await savePayroll(updated);
  }

  async function updateHeldPay(repId, val) {
    const rd = getRepData(repId);
    const updated = { ...payrollData, [repId]: { ...rd, heldPay: Number(val) || 0 } };
    setPayrollData(updated); await savePayroll(updated);
  }

  async function submitPayroll() {
    setSaving(true);
    // Save updated bucket balances for each rep
    const updatedRepData = { ...payrollData };
    repRows.forEach(r => {
      const rd = getRepData(r.rep.id);
      updatedRepData[r.rep.id] = {
        ...rd,
        chargebackBucketUsed: r.bucketTotal, // persist the new bucket total
      };
    });
    await setDoc(doc(db, 'payrollWeeks', weekKey), {
      weekStart: weekStart.toISOString(),
      weekEnd: weekEnd.toISOString(),
      repData: updatedRepData,
      submitted: true,
      submittedAt: new Date().toISOString(),
      totals,
      invoiceTotal,
    });
    // Also update a global bucket balance per rep so it carries forward week to week
    const batch = writeBatch(db);
    repRows.forEach(r => {
      batch.set(doc(db, 'repBuckets', r.rep.id), {
        repId: r.rep.id,
        repName: r.rep.name,
        bucketBalance: r.bucketTotal,
        bucketLimit: r.bucketLimit,
        lastUpdated: new Date().toISOString(),
        lastWeek: weekKey,
      });
    });
    await batch.commit();
    setSubmitted(true);
    setSaving(false);
  }

  function exportCSV() {
    const lines = ['Agent,Commissions,Additions,Chargebacks,Deductions,Advances,Held Pay,Total'];
    repRows.forEach(r => lines.push(`"${r.rep.name}",${r.commissions},${r.additions},${r.chargebacksOwed},${r.deductions},${r.advances},${r.heldPay},${r.total}`));
    lines.push(`Total,${totals.commissions},${totals.additions},${totals.chargebacks},${totals.deductions},${totals.advances},,${totals.total}`);
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `payroll_${weekKey}.csv`; a.click();
  }

  if (loading) return <div style={{ padding: 60, color: '#555', marginLeft: 220 }}>Loading payroll...</div>;

  return (
    <div style={S.page}>
      {addModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <div style={S.modalTitle}>Add {addModal.type} — {reps.find(r => r.id === addModal.repId)?.name}</div>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Amount ($)</label>
              <input style={S.inp} type="number" placeholder="0.00" value={modalForm.amount} onChange={e => setModalForm(f => ({ ...f, amount: e.target.value }))} autoFocus />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Note</label>
              <input style={S.inp} placeholder="e.g. Admin payout, Hourly pay..." value={modalForm.note} onChange={e => setModalForm(f => ({ ...f, note: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={S.cancelBtn} onClick={() => { setAddModal(null); setModalForm({ amount: '', note: '' }); }}>Cancel</button>
              <button style={{ ...S.confirmBtn, background: addModal.type === 'deduction' ? '#c0392b' : addModal.type === 'advance' ? '#e67e22' : '#00d4a0' }}
                onClick={() => addEntry(addModal.repId, addModal.type)} disabled={saving}>
                Add {addModal.type}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={S.header}>
        <div>
          <h1 style={S.title}>Payroll</h1>
          <p style={S.sub}>
            {format(weekStart, 'MM/dd/yy')} – {format(weekEnd, 'MM/dd/yy')} · {weekOrders.length} orders
            {submitted && <span style={S.subBadge}>✓ Submitted</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.wkBtn} onClick={() => setWeekOffset(w => w + 1)}>← Prev</button>
          <button style={S.wkBtn} onClick={() => setWeekOffset(w => Math.max(0, w - 1))} disabled={weekOffset === 0}>Next →</button>
          <button style={S.wkBtn} onClick={exportCSV}>Export CSV</button>
          <button style={{ ...S.wkBtn, color: recalcDone !== null ? '#00d4a0' : '#f5a623', borderColor: recalcDone !== null ? '#2a5a3a' : '#3a3a20' }} onClick={recalculateCommissions} disabled={recalculating}>
            {recalculating ? 'Recalculating...' : recalcDone !== null ? `✓ Fixed ${recalcDone} orders` : '⟳ Recalc commissions'}
          </button>
          {!submitted && <button style={S.submitBtn} onClick={submitPayroll} disabled={saving}>{saving ? 'Saving...' : 'Submit payroll'}</button>}
        </div>
      </div>

      <div style={S.summaryCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 40px' }}>
          <div>
            <div style={S.sumTitle}>Invoice totals</div>
            <div style={S.sumRow}><span>Commission</span><span>{fmt(invoiceTotal)}</span></div>
            <div style={S.sumRow}><span style={{ fontWeight: 600, color: '#fff' }}>Total invoice</span><span style={{ fontWeight: 600 }}>{fmt(invoiceTotal)}</span></div>
          </div>
          <div>
            <div style={S.sumTitle}>Payout totals</div>
            <div style={S.sumRow}><span>Commissions</span><span style={{ color: '#ff6b6b' }}>{fmt(totals.commissions)}</span></div>
            <div style={S.sumRow}><span>Additions</span><span style={{ color: '#00d4a0' }}>{fmt(totals.additions)}</span></div>
            <div style={S.sumRow}><span>Chargebacks</span><span style={{ color: '#ff6b6b' }}>({fmt(totals.chargebacks)})</span></div>
            <div style={S.sumRow}><span>Deductions</span><span style={{ color: '#ff6b6b' }}>({fmt(totals.deductions)})</span></div>
            <div style={S.sumRow}><span>Advances</span><span style={{ color: '#f5a623' }}>({fmt(totals.advances)})</span></div>
            <div style={{ borderTop: '1px solid #2a2a3a', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 600 }}>
              <span style={{ color: '#fff' }}>Total payout</span><span style={{ color: '#f5a623' }}>{fmt(totals.total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          style={{ ...S.inp, width: 260, background: '#1a1a24' }}
          placeholder="Search agent name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', fontSize: 13 }} onClick={() => setSearch('')}>✕ Clear</button>}
        <span style={{ fontSize: 12, color: '#555' }}>{filteredRows.length} of {repRows.length} agents</span>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <SortTh col="name" label="Agent" />
              <SortTh col="orders" label="Status" />
              <SortTh col="commissions" label="Commissions" />
              <SortTh col="chargebacksTotal" label="Chargebacks" />
              <th style={S.th}>Additions</th>
              <SortTh col="chargebacks" label="Chargebacks owed" />
              <th style={S.th}>CB bucket</th>
              <SortTh col="advances" label="Advances" />
              <th style={S.th}>Held pay</th>
              <SortTh col="deductions" label="Deductions" />
              <SortTh col="total" label="Final pay" />
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(({ rep, myOrders, commissions, chargebacks, chargebacksOwed, absorbedByBucket, bucketUsed, bucketTotal, bucketLimit, bucketWithheldThisWeek, pd, additions, deductions, advances, heldPay, total }) => {
              const completed = myOrders.filter(o => o.status === 'Completed').length;
              return (
                <tr key={rep.id}>
                  <td style={S.td}>
                    <div style={S.agentTag}>{rep.name.toUpperCase()}</div>
                    <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>{rep.role || 'Agent'}</div>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#1a2e24', color: '#00d4a0' }}>
                      {myOrders.length > 0 ? 'Active' : 'No orders'}
                    </span>
                    <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>{myOrders.length} orders · {completed} done</div>
                  </td>
                  <td style={S.td}><span style={S.commTag}>{fmt(commissions)}</span></td>
                  <td style={S.td}>{chargebacks > 0 ? <span style={S.dedTag}>{fmt(chargebacks)}</span> : <span style={{ color: '#444' }}>$0</span>}</td>
                  <td style={S.td}>
                    {(pd.additions || []).map((e, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                        <span style={S.addTag}>{fmt(e.amount)}</span>
                        {e.note && <span style={S.noteTag}>{e.note}</span>}
                        {!submitted && <button style={S.xBtn} onClick={() => removeEntry(rep.id, 'addition', i)}>✕</button>}
                      </div>
                    ))}
                    {!submitted && <button style={S.plusBtn} onClick={() => { setAddModal({ repId: rep.id, type: 'addition' }); setModalForm({ amount: '', note: '' }); }}>+ Add</button>}
                  </td>
                  <td style={S.td}>
                    {chargebacksOwed > 0 ? <span style={S.dedTag}>{fmt(chargebacksOwed)}</span> : <span style={{ color: '#444' }}>$0</span>}
                    {absorbedByBucket > 0 && <div style={{ fontSize: 9, color: '#555', marginTop: 2 }}>{fmt(absorbedByBucket)} in bucket</div>}
                  </td>
                  <td style={S.td}>
                    <div style={{ background: '#1a1a24', borderRadius: 3, height: 5, width: 80, marginBottom: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, background: bucketTotal >= bucketLimit ? '#ff6b6b' : '#f5a623', width: `${Math.min(100, (bucketTotal / bucketLimit) * 100)}%` }} />
                    </div>
                    <div style={{ fontSize: 9, color: '#666' }}>{fmt(bucketTotal)} / {fmt(bucketLimit)}</div>
                    {bucketWithheldThisWeek > 0 && (
                      <div style={{ fontSize: 9, color: '#f5a623', marginTop: 2 }}>+{fmt(bucketWithheldThisWeek)} this week</div>
                    )}
                    {bucketTotal >= bucketLimit && (
                      <div style={{ fontSize: 9, color: '#00d4a0', marginTop: 2 }}>✓ Max reached</div>
                    )}
                  </td>
                  <td style={S.td}>
                    {(pd.advances || []).map((e, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                        <span style={S.advTag}>{fmt(e.amount)}</span>
                        {e.note && <span style={S.noteTag}>{e.note}</span>}
                        {!submitted && <button style={S.xBtn} onClick={() => removeEntry(rep.id, 'advance', i)}>✕</button>}
                      </div>
                    ))}
                    {!submitted && <button style={{ ...S.plusBtn, color: '#f5a623' }} onClick={() => { setAddModal({ repId: rep.id, type: 'advance' }); setModalForm({ amount: '', note: '' }); }}>+ Add</button>}
                  </td>
                  <td style={S.td}>
                    {submitted
                      ? <span style={{ color: heldPay > 0 ? '#f5a623' : '#444' }}>{fmt(heldPay)}</span>
                      : <input style={S.heldInp} type="number" value={pd.heldPay || ''} placeholder="0"
                          onChange={e => updateHeldPay(rep.id, e.target.value)} />}
                  </td>
                  <td style={S.td}>
                    {(pd.deductions || []).map((e, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                        <span style={S.dedTag}>{fmt(e.amount)}</span>
                        {e.note && <span style={S.noteTag}>{e.note}</span>}
                        {!submitted && <button style={S.xBtn} onClick={() => removeEntry(rep.id, 'deduction', i)}>✕</button>}
                      </div>
                    ))}
                    {!submitted && <button style={{ ...S.plusBtn, color: '#ff6b6b' }} onClick={() => { setAddModal({ repId: rep.id, type: 'deduction' }); setModalForm({ amount: '', note: '' }); }}>+ Add</button>}
                  </td>
                  <td style={{ ...S.td, fontWeight: 700, fontSize: 14, color: total >= 0 ? '#fff' : '#ff6b6b' }}>{fmt(total)}</td>
                </tr>
              );
            })}
            {filteredRows.length === 0 && <tr><td colSpan={10} style={{ ...S.td, textAlign: 'center', color: '#444', padding: 40 }}>{repRows.length === 0 ? 'No payroll data for this week' : 'No agents match your search'}</td></tr>}
          </tbody>
          {repRows.length > 0 && (
            <tfoot>
              <tr style={{ borderTop: '2px solid #2a2a3a' }}>
                <td colSpan={2} style={{ ...S.td, fontWeight: 600, color: '#fff' }}>Totals</td>
                <td style={{ ...S.td, color: '#5599cc', fontWeight: 600 }}>{fmt(totals.commissions)}</td>
                <td style={S.td}>—</td>
                <td style={{ ...S.td, color: '#00d4a0', fontWeight: 600 }}>{fmt(totals.additions)}</td>
                <td style={{ ...S.td, color: '#ff6b6b', fontWeight: 600 }}>({fmt(totals.chargebacks)})</td>
                <td style={S.td}>—</td>
                <td style={{ ...S.td, color: '#f5a623', fontWeight: 600 }}>({fmt(totals.advances)})</td>
                <td style={S.td}>—</td>
                <td style={{ ...S.td, color: '#ff6b6b', fontWeight: 600 }}>({fmt(totals.deductions)})</td>
                <td style={{ ...S.td, color: '#f5a623', fontWeight: 700, fontSize: 14 }}>{fmt(totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 8 },
  subBadge: { fontSize: 10, background: '#1a2e24', color: '#00d4a0', padding: '2px 8px', borderRadius: 5 },
  wkBtn: { background: '#13131a', border: '1px solid #2a2a3a', borderRadius: 8, padding: '7px 14px', color: '#888', fontSize: 12, cursor: 'pointer' },
  submitBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '8px 18px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  summaryCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '16px 20px', marginBottom: 16, maxWidth: 600 },
  sumTitle: { fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  sumRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 4 },
  tableWrap: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 1000 },
  th: { textAlign: 'left', padding: '9px 10px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a', whiteSpace: 'nowrap' },
  td: { padding: '10px 10px', fontSize: 12, color: '#ccc', borderBottom: '1px solid #111', verticalAlign: 'top' },
  agentTag: { background: '#0a2a3a', color: '#00d4a0', padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, display: 'inline-block' },
  commTag: { background: '#1a2a3a', color: '#5599cc', padding: '3px 8px', borderRadius: 4, fontSize: 12 },
  addTag: { background: '#1a2e24', color: '#00d4a0', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  dedTag: { background: '#2a1515', color: '#ff6b6b', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  advTag: { background: '#2a2010', color: '#f5a623', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  noteTag: { fontSize: 9, color: '#555', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  xBtn: { background: 'transparent', border: 'none', color: '#444', cursor: 'pointer', fontSize: 10, padding: 0 },
  plusBtn: { background: 'transparent', border: '1px dashed #2a2a3a', borderRadius: 4, padding: '2px 7px', color: '#00d4a0', fontSize: 10, cursor: 'pointer', marginTop: 2 },
  heldInp: { background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 5, padding: '3px 7px', color: '#f5a623', fontSize: 11, width: 65, outline: 'none' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#13131a', border: '1px solid #2a2a3a', borderRadius: 14, padding: '28px', width: 380 },
  modalTitle: { fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 20 },
  label: { display: 'block', fontSize: 11, color: '#666', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 },
  inp: { width: '100%', background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  cancelBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 8, padding: '8px 16px', color: '#888', fontSize: 13, cursor: 'pointer' },
  confirmBtn: { border: 'none', borderRadius: 8, padding: '8px 16px', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
